import React, { useState, useMemo } from 'react';
import styles from './Tabs.module.css';

interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  tabs: TabItem[];
  defaultTabId?: string;
  onChange?: (tabId: string) => void;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTabId,
  onChange,
  className,
}) => {
  const validDefaultTab = defaultTabId && tabs.some((t) => t.id === defaultTabId) ? defaultTabId : tabs[0]?.id;
  const [activeTabId, setActiveTabId] = useState<string>(validDefaultTab || '');

  const activeTab = useMemo(
    () => tabs.find((tab) => tab.id === activeTabId),
    [activeTabId, tabs]
  );

  const handleTabChange = (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (!tab?.disabled) {
      setActiveTabId(tabId);
      onChange?.(tabId);
    }
  };

  return (
    <div className={`${styles.tabsContainer} ${className || ''}`}>
      <div className={styles.tabList} role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            id={`tab-${tab.id}`}
            role="tab"
            aria-selected={activeTabId === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            disabled={tab.disabled}
            className={`${styles.tab} ${activeTabId === tab.id ? styles.active : ''} ${
              tab.disabled ? styles.disabled : ''
            }`}
            onClick={() => handleTabChange(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {activeTab && (
          <div
            id={`tabpanel-${activeTab.id}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab.id}`}
            className={styles.tabPanel}
          >
            {activeTab.content}
          </div>
        )}
      </div>
    </div>
  );
};

// Alternative simpler API using children
interface SimpleTabsProps {
  defaultTabIndex?: number;
  onChange?: (index: number) => void;
  children: React.ReactElement<{ label: string }>[];
}

export const SimpleTabs: React.FC<SimpleTabsProps> = ({
  defaultTabIndex = 0,
  onChange,
  children,
}) => {
  const [activeIndex, setActiveIndex] = useState(defaultTabIndex);

  const tabs = useMemo(
    () =>
      React.Children.toArray(children).map((child, index) => {
        const element = child as React.ReactElement<{ label: string; children: React.ReactNode }>;
        return {
          id: String(index),
          label: element.props.label || `Tab ${index + 1}`,
          content: element.props.children,
        };
      }),
    [children]
  );

  const handleTabChange = (tabId: string) => {
    const newIndex = parseInt(tabId, 10);
    setActiveIndex(newIndex);
    onChange?.(newIndex);
  };

  return <Tabs tabs={tabs} defaultTabId={String(activeIndex)} onChange={handleTabChange} />;
};
